import type { TabComponentEnabled } from '@/helpers/featureFlags'

/** LD shape for `enable-about-faq-by-storefront-id` (see `featureFlags.tsx`). */
type AboutFaqLd = TabComponentEnabled

function isStorefrontIdInAllowlist(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd,
  allowlistKey: keyof Exclude<AboutFaqLd, false>
): boolean {
  if (
    typeof storefrontId !== 'string' ||
    storefrontId.length === 0 ||
    ldValue === false
  ) {
    return false
  }
  // `ldValue` is a LaunchDarkly-served JSON blob, not a compile-time value —
  // an older allowlist shape configured in the LD dashboard (e.g. one missing
  // the newer condition/shipping/device-unlocking-terms keys) would otherwise
  // throw here instead of just treating that tab as disabled.
  const allowlist = ldValue[allowlistKey] as string[] | undefined
  return allowlist?.includes(storefrontId) ?? false
}

/**
 * LD allowlist for legacy About tab UI (`StorefrontTabs` + unauth Costco fold).
 */
export function isLegacyAboutTabEnabledForStorefront(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd
): boolean {
  return isStorefrontIdInAllowlist(
    storefrontId,
    ldValue,
    'aboutEnabledStorefrontIds'
  )
}

/**
 * LD allowlist for legacy FAQ tab UI (`StorefrontTabs`).
 */
export function isLegacyFaqTabEnabledForStorefront(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd
): boolean {
  return isStorefrontIdInAllowlist(
    storefrontId,
    ldValue,
    'faqEnabledStorefrontIds'
  )
}

/**
 * LD allowlist for Condition tab UI (`StorefrontTabs`).
 */
export function isConditionTabEnabledForStorefront(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd
): boolean {
  return isStorefrontIdInAllowlist(
    storefrontId,
    ldValue,
    'conditionsEnabledStorefrontIds'
  )
}

/**
 * LD allowlist for Shipping tab UI (`StorefrontTabs`).
 */
export function isShippingTabEnabledForStorefront(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd
): boolean {
  return isStorefrontIdInAllowlist(
    storefrontId,
    ldValue,
    'shippingEnabledStorefrontIds'
  )
}

/**
 * LD allowlist for Device Unlocking Terms tab UI (`StorefrontTabs`).
 */
export function isDeviceUnlockingTermsTabEnabledForStorefront(
  storefrontId: string | undefined,
  ldValue: AboutFaqLd
): boolean {
  return isStorefrontIdInAllowlist(
    storefrontId,
    ldValue,
    'deviceUnlockingTermsEnabledStorefrontIds'
  )
}
